import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BarChart3Icon,
  GraduationCapIcon,
  Loader2Icon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
} from 'lucide-react';
import type { Course } from '@kennisbank/shared';
import { Layout } from '../components/Layout.js';
import { ApiError } from '../api/client.js';
import { listCourses, deleteCourse } from '../components/elearning/AdminElearningApi.js';
import { AdminStatusChip, AdminAudienceChip } from '../components/elearning/AdminElearningChips.js';
import { AdminCourseEditor } from '../components/elearning/AdminCourseEditor.js';
import { AdminCourseDashboard } from '../components/elearning/AdminCourseDashboard.js';
import { AdminWebinarUploads } from '../components/elearning/AdminWebinarUploads.js';

/**
 * Beheer → E-learning (contentbeheer, suppohrt_admin).
 *
 * Eén pagina met drie interne views (lijst / editor / dashboard), gestuurd
 * via query-parameters zodat deep-links en de browser-terugknop werken zonder
 * nieuwe router-routes toe te voegen:
 *   (geen params)                    → cursusoverzicht
 *   ?course=new&view=editor          → nieuwe cursus
 *   ?course=<id>&view=editor         → cursus bewerken
 *   ?course=<id>&view=dashboard      → voortgangsdashboard
 *
 * Verwacht route-pad (wiring in App.tsx door de hoofdsessie): /beheer/e-learning
 *
 * RBAC: suppohrt_admin. De route wordt achter RoleGuard gehangen; de backend
 * hercontroleert en geeft 404 als ELEARNING_ENABLED uit staat.
 */
export function BeheerElearningPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const view = params.get('view');
  const courseParam = params.get('course');

  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadCourses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listCourses();
      setCourses(res.courses);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Cursussen konden niet geladen worden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!view) void loadCourses();
  }, [view, loadCourses]);

  function goList(): void {
    setParams({});
  }
  function openEditor(id: string): void {
    setParams({ course: id, view: 'editor' });
  }
  function openNew(): void {
    setParams({ course: 'new', view: 'editor' });
  }
  function openDashboard(id: string): void {
    setParams({ course: id, view: 'dashboard' });
  }

  async function handleDelete(course: Course): Promise<void> {
    if (
      !window.confirm(
        `Weet je zeker dat je "${course.name}" wilt verwijderen? Alle modules, lessen, vragen en voortgang worden verwijderd.`,
      )
    )
      return;
    setDeletingId(course.id);
    try {
      await deleteCourse(course.id);
      await loadCourses();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Verwijderen mislukt.');
    } finally {
      setDeletingId(null);
    }
  }

  const courseName = courseParam ? courses.find((c) => c.id === courseParam)?.name : undefined;

  // ── Editor view ──
  if (view === 'editor') {
    return (
      <Layout>
        <div className="thin-scroll h-full overflow-y-auto">
          <AdminCourseEditor
            courseId={courseParam === 'new' ? null : courseParam}
            onBack={goList}
            onCourseCreated={(id) => setParams({ course: id, view: 'editor' })}
            onOpenDashboard={openDashboard}
          />
        </div>
      </Layout>
    );
  }

  // ── Dashboard view ──
  if (view === 'dashboard' && courseParam) {
    return (
      <Layout>
        <div className="thin-scroll h-full overflow-y-auto">
          <AdminCourseDashboard courseId={courseParam} courseName={courseName} onBack={goList} />
        </div>
      </Layout>
    );
  }

  // ── List view ──
  return (
    <Layout>
      <div className="thin-scroll h-full overflow-y-auto">
        <div className="mx-auto w-full max-w-5xl px-4 py-6">
          <header className="mb-6 flex items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-ink-900">Beheer E-learning</h1>
              <p className="mt-1 text-sm text-ink-700/70">
                Maak en beheer cursussen met modules, lessen en vragen. Zet een cursus op actief
                zodra je klaar bent.
              </p>
            </div>
            <button
              type="button"
              onClick={openNew}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-brand-700 px-5 py-2 text-sm font-semibold text-white hover:bg-brand-800"
            >
              <PlusIcon size={15} /> Nieuwe cursus
            </button>
          </header>

          {error && (
            <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2Icon className="h-6 w-6 animate-spin text-ink-700/40" />
            </div>
          ) : courses.length === 0 ? (
            <div className="rounded-xl border border-brand-100 bg-white py-16 text-center shadow-card">
              <GraduationCapIcon className="mx-auto mb-4 h-10 w-10 text-ink-700/30" />
              <h3 className="mb-1 text-lg font-semibold text-ink-900">Nog geen cursussen</h3>
              <p className="mx-auto mb-4 max-w-sm text-sm text-ink-700/70">
                Maak de eerste cursus aan om modules, lessen en vragen toe te voegen.
              </p>
              <button
                type="button"
                onClick={openNew}
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-700 px-5 py-2 text-sm font-semibold text-white hover:bg-brand-800"
              >
                <PlusIcon size={15} /> Nieuwe cursus
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-brand-100 bg-white shadow-card">
              <table className="w-full min-w-[640px] text-sm">
                <thead className="bg-brand-50/50 text-left text-[11px] uppercase tracking-wider text-ink-800/60">
                  <tr>
                    <th className="px-4 py-3">Naam</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Doelgroep</th>
                    <th className="px-4 py-3">Laatst bijgewerkt</th>
                    <th className="px-4 py-3 text-right">Acties</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-50">
                  {courses.map((c) => (
                    <tr key={c.id} className="hover:bg-brand-50/40">
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => openEditor(c.id)}
                          className="text-left font-medium text-ink-900 hover:text-brand-700 hover:underline"
                        >
                          {c.name}
                        </button>
                        {c.description && (
                          <p className="mt-0.5 line-clamp-1 text-xs text-ink-700/60">
                            {c.description}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <AdminStatusChip status={c.status} />
                      </td>
                      <td className="px-4 py-3">
                        <AdminAudienceChip audience={c.audience} />
                      </td>
                      <td className="px-4 py-3 text-ink-700/70">
                        {new Date(c.updatedAt ?? c.createdAt).toLocaleString('nl-NL')}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <div className="inline-flex gap-2">
                          <button
                            type="button"
                            onClick={() => openDashboard(c.id)}
                            className="inline-flex items-center gap-1 rounded-md border border-brand-100 px-2 py-1 text-xs text-ink-800 hover:bg-brand-50"
                          >
                            <BarChart3Icon size={13} /> Voortgang
                          </button>
                          <button
                            type="button"
                            onClick={() => openEditor(c.id)}
                            className="inline-flex items-center gap-1 rounded-md border border-brand-100 px-2 py-1 text-xs text-ink-800 hover:bg-brand-50"
                          >
                            <PencilIcon size={13} /> Bewerken
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleDelete(c)}
                            disabled={deletingId === c.id}
                            aria-label={`Verwijder ${c.name}`}
                            className="inline-flex items-center justify-center rounded-md border border-red-100 bg-red-50 p-1.5 text-red-700 hover:bg-red-100 disabled:opacity-50"
                          >
                            {deletingId === c.id ? (
                              <Loader2Icon size={14} className="animate-spin" />
                            ) : (
                              <Trash2Icon size={14} />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <AdminWebinarUploads />
        </div>
      </div>
    </Layout>
  );
}
